/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { css } from '@emotion/react'
import { App } from 'antd'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import type {
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import colors from '@sportninja/common/constants/appColors'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import { CheckboxOutlineIcon, CheckboxCheckedIcon } from './icons'

// --- DnD sub-components (local) ---

const GripIcon: React.FC<{ color?: string }> = ({
  color = colors.NEUTRAL_100,
}) => (
  <svg width='10' height='16' viewBox='0 0 10 16' fill='none'>
    <circle cx='2' cy='2' r='1.5' fill={color} />
    <circle cx='8' cy='2' r='1.5' fill={color} />
    <circle cx='2' cy='8' r='1.5' fill={color} />
    <circle cx='8' cy='8' r='1.5' fill={color} />
    <circle cx='2' cy='14' r='1.5' fill={color} />
    <circle cx='8' cy='14' r='1.5' fill={color} />
  </svg>
)

const SortableDivisionRow: React.FC<{
  id: string
  children: (dragProps: {
    attributes: DraggableAttributes
    listeners: DraggableSyntheticListeners
  }) => React.ReactNode
}> = ({ id, children }) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  )
}

const DragHandle: React.FC = () => (
  <div css={dragHandleStyles} aria-hidden>
    <GripIcon />
  </div>
)

// --- Main component ---

interface DivisionSidebarProps {
  divisions: DivisionItem[]
  divisionsByConference: Map<string, DivisionItem[]>
  readyPercentage: number
  readyCount: number
  totalCount: number
  selectedDivisionId: string | null
  onSelectDivision: (divisionId: string) => void
  onBulkSetReady?: (divisionIds: string[], isReady: boolean) => Promise<number>
  loading?: boolean
  divisionPriorityOrder?: string[]
  onDivisionPriorityChange?: (newOrder: string[]) => void
}

const DivisionSidebar: React.FC<DivisionSidebarProps> = ({
  divisions,
  divisionsByConference,
  readyPercentage,
  readyCount,
  totalCount,
  selectedDivisionId,
  onSelectDivision,
  onBulkSetReady,
  loading,
  divisionPriorityOrder,
  onDivisionPriorityChange,
}) => {
  const { message } = App.useApp()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // DnD state
  const [activeDragId, setActiveDragId] = useState<UniqueIdentifier | null>(
    null
  )
  const hasDnd = !!divisionPriorityOrder && !!onDivisionPriorityChange
  const isDndDisabled = !!searchQuery

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Ordered divisions based on priority
  const orderedDivisionIds = useMemo(() => {
    if (!divisionPriorityOrder || divisionPriorityOrder.length === 0) {
      return divisions.map((d) => d.id)
    }
    const existing = new Set(divisionPriorityOrder)
    const appended = divisions
      .filter((d) => !existing.has(d.id))
      .map((d) => d.id)
    return [...divisionPriorityOrder, ...appended]
  }, [divisionPriorityOrder, divisions])

  const orderedDivisions = useMemo(() => {
    const idxMap = new Map(orderedDivisionIds.map((id, i) => [id, i]))
    return [...divisions].sort(
      (a, b) =>
        (idxMap.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (idxMap.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )
  }, [divisions, orderedDivisionIds])

  // Only Ready divisions get priority numbers (1-based)
  const readyPriorityMap = useMemo(() => {
    const map = new Map<string, number>()
    let rank = 1
    for (const id of orderedDivisionIds) {
      const div = divisions.find((d) => d.id === id)
      if (div?.isReady) {
        map.set(id, rank++)
      }
    }
    return map
  }, [orderedDivisionIds, divisions])

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveDragId(null)
      if (!over || active.id === over.id || !onDivisionPriorityChange) {
        return
      }
      const order = [...orderedDivisionIds]
      const oldIndex = order.indexOf(String(active.id))
      const newIndex = order.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) {
        return
      }
      onDivisionPriorityChange(arrayMove(order, oldIndex, newIndex))
    },
    [orderedDivisionIds, onDivisionPriorityChange]
  )

  const activeDragDivision = useMemo(
    () =>
      activeDragId
        ? orderedDivisions.find((d) => d.id === String(activeDragId))
        : null,
    [activeDragId, orderedDivisions]
  )

  // Filter divisions by search
  const filteredDivisions = searchQuery
    ? orderedDivisions.filter((d) =>
        d.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : orderedDivisions

  // Build filtered conference map
  const filteredByConference = new Map<string, DivisionItem[]>()
  if (searchQuery) {
    filteredDivisions.forEach((div) => {
      const group = div.divisionGroup || 'GENERAL'
      const existing = filteredByConference.get(group) || []
      existing.push(div)
      filteredByConference.set(group, existing)
    })
  }

  const conferenceMap = searchQuery
    ? filteredByConference
    : divisionsByConference

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [searchOpen])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (filteredDivisions.length === 0) {
        return
      }
      const currentIndex = filteredDivisions.findIndex(
        (d) => d.id === selectedDivisionId
      )

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex =
          currentIndex < filteredDivisions.length - 1 ? currentIndex + 1 : 0
        onSelectDivision(filteredDivisions[nextIndex].id)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : filteredDivisions.length - 1
        onSelectDivision(filteredDivisions[prevIndex].id)
      }
    },
    [filteredDivisions, selectedDivisionId, onSelectDivision]
  )

  useEffect(() => {
    const el = listRef.current
    if (!el) {
      return
    }
    el.addEventListener('keydown', handleKeyDown)
    return () => el.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Only one conference group and it's GENERAL → show flat list
  const hasConferenceGrouping =
    conferenceMap.size > 1 ||
    (conferenceMap.size === 1 && !conferenceMap.has('GENERAL'))

  // Multi-select helpers
  const toggleCheck = useCallback((divisionId: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(divisionId)) {
        next.delete(divisionId)
      } else {
        next.add(divisionId)
      }
      return next
    })
  }, [])

  const allFilteredChecked =
    filteredDivisions.length > 0 &&
    filteredDivisions.every((d) => checkedIds.has(d.id))

  const toggleSelectAll = useCallback(() => {
    if (allFilteredChecked) {
      setCheckedIds(new Set())
    } else {
      setCheckedIds(new Set(filteredDivisions.map((d) => d.id)))
    }
  }, [allFilteredChecked, filteredDivisions])

  const handleBulkAction = useCallback(
    async (isReady: boolean) => {
      if (!onBulkSetReady || checkedIds.size === 0) {
        return
      }
      setBulkLoading(true)
      try {
        await onBulkSetReady(Array.from(checkedIds), isReady)
        setCheckedIds(new Set())
      } catch (err) {
        message.error(
          err instanceof Error
            ? err.message
            : 'Failed to bulk update ready status'
        )
      } finally {
        setBulkLoading(false)
      }
    },
    [onBulkSetReady, checkedIds, message]
  )

  // Render a single division row
  const renderDivisionRow = (
    div: DivisionItem,
    dragProps?: {
      attributes: DraggableAttributes
      listeners: DraggableSyntheticListeners
    }
  ) => (
    <DivisionRow
      key={div.id}
      division={div}
      isSelected={div.id === selectedDivisionId}
      isChecked={checkedIds.has(div.id)}
      showCheckbox={!!onBulkSetReady}
      onToggleCheck={() => toggleCheck(div.id)}
      onClick={() => onSelectDivision(div.id)}
      priorityIndex={readyPriorityMap.get(div.id)}
      showDragHandle={hasDnd && !isDndDisabled && div.isReady}
      dragAttributes={dragProps?.attributes}
      dragListeners={dragProps?.listeners}
    />
  )

  // Wrap a row in sortable wrapper (when DnD is active)
  const renderSortableRow = (div: DivisionItem) => (
    <SortableDivisionRow key={div.id} id={div.id}>
      {(dragProps) => renderDivisionRow(div, dragProps)}
    </SortableDivisionRow>
  )

  // Render division list content
  const renderDivisionList = () => {
    if (loading && filteredDivisions.length === 0) {
      return <div css={emptyState}>Loading...</div>
    }
    if (filteredDivisions.length === 0) {
      return (
        <div css={emptyState}>
          {searchQuery ? 'No matching divisions' : 'No divisions added'}
        </div>
      )
    }

    // When DnD active and no search — flat sortable list (no conference headers)
    if (hasDnd && !isDndDisabled) {
      return filteredDivisions.map(renderSortableRow)
    }

    // Search active or no DnD — plain rows with optional conference grouping
    if (hasConferenceGrouping) {
      return Array.from(conferenceMap.entries()).map(([group, divs]) => (
        <div key={group} css={conferenceGroup}>
          <div css={conferenceHeader}>
            {group === 'GENERAL' ? 'ALL DIVISIONS' : group.toUpperCase()}
          </div>
          {divs.map((div) => renderDivisionRow(div))}
        </div>
      ))
    }

    return filteredDivisions.map((div) => renderDivisionRow(div))
  }

  return (
    <div css={sidebarContainer}>
      {/* Header */}
      <div css={headerSection}>
        <div css={headerStyles}>
          <div css={headerLeft}>
            <div css={headerTitleRow}>
              {onBulkSetReady && filteredDivisions.length > 0 && (
                <div
                  css={checkboxHitArea}
                  onClick={toggleSelectAll}
                  role='checkbox'
                  aria-checked={allFilteredChecked}
                  aria-label='Select all divisions'
                  title='Select all divisions'
                >
                  {allFilteredChecked ? (
                    <CheckboxCheckedIcon size={20} />
                  ) : (
                    <CheckboxOutlineIcon size={20} />
                  )}
                </div>
              )}
              <div css={headerTitle}>DIVISIONS</div>
            </div>
            <div css={headerSubtitle}>
              Organize and prioritize divisions for scheduling
            </div>
          </div>
          <button
            css={searchButtonStyles}
            onClick={() => {
              setSearchOpen(!searchOpen)
              if (searchOpen) {
                setSearchQuery('')
              }
            }}
            title='Search divisions'
          >
            <i className='fas fa-search' />
          </button>
        </div>
      </div>

      {/* Search input */}
      {searchOpen && (
        <div css={searchContainer}>
          <input
            ref={searchInputRef}
            css={searchInput}
            type='text'
            placeholder='Search divisions...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Ready percentage progress bar */}
      <div css={progressCard}>
        <div css={progressTitle}>READY PERCENTAGE</div>
        <div css={progressRow}>
          <div css={progressBarBg}>
            <div
              css={progressBarFill}
              style={{ width: `${readyPercentage}%` }}
            />
          </div>
          <span css={progressCount}>
            {Math.round(readyPercentage)}% ({readyCount}/{totalCount})
          </span>
        </div>
      </div>

      {/* Bulk action toolbar — at the top, above division list */}
      {checkedIds.size > 0 && onBulkSetReady && (
        <div css={bulkToolbar}>
          <div css={bulkToolbarTop}>
            <span css={bulkCountText}>{checkedIds.size} selected</span>
            <button css={deselectLink} onClick={() => setCheckedIds(new Set())}>
              Deselect
            </button>
          </div>
          <div css={bulkToolbarActions}>
            <button
              css={bulkReadyButton}
              onClick={() => handleBulkAction(true)}
              disabled={bulkLoading}
            >
              {bulkLoading ? 'Updating...' : 'Mark Ready'}
            </button>
            <button
              css={bulkDraftButton}
              onClick={() => handleBulkAction(false)}
              disabled={bulkLoading}
            >
              {bulkLoading ? 'Updating...' : 'Mark Draft'}
            </button>
          </div>
        </div>
      )}

      <hr css={dividerStyles} />

      {/* Division list */}
      <div css={listContainer} ref={listRef} tabIndex={0}>
        {hasDnd && !isDndDisabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedDivisionIds}
              strategy={verticalListSortingStrategy}
            >
              {renderDivisionList()}
            </SortableContext>
            <DragOverlay>
              {activeDragDivision ? (
                <div css={dragOverlayCard}>
                  <GripIcon color={colors.NEUTRAL_50} />
                  <span css={dragOverlayName}>{activeDragDivision.name}</span>
                  <span
                    css={activeDragDivision.isReady ? readyBadge : draftBadge}
                  >
                    <i
                      className={
                        activeDragDivision.isReady
                          ? 'fas fa-check'
                          : 'fas fa-pen'
                      }
                      css={badgeIcon}
                    />
                    {activeDragDivision.isReady ? 'Ready' : 'Draft'}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          renderDivisionList()
        )}
      </div>
    </div>
  )
}

// --- DivisionRow sub-component ---

interface DivisionRowProps {
  division: DivisionItem
  isSelected: boolean
  isChecked: boolean
  showCheckbox: boolean
  onToggleCheck: () => void
  onClick: () => void
  priorityIndex?: number
  showDragHandle?: boolean
  dragAttributes?: DraggableAttributes
  dragListeners?: DraggableSyntheticListeners
}

const DivisionRow: React.FC<DivisionRowProps> = ({
  division,
  isSelected,
  isChecked,
  showCheckbox,
  onToggleCheck,
  onClick,
  priorityIndex,
  showDragHandle,
  dragAttributes,
  dragListeners,
}) => {
  return (
    <div
      css={[
        rowStyles,
        isSelected && rowSelectedStyles,
        showDragHandle && rowDraggableStyles,
      ]}
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      {...(showDragHandle ? dragAttributes : {})}
      {...(showDragHandle ? dragListeners : {})}
    >
      {(showDragHandle || priorityIndex != null) && (
        <div css={priorityColumnStyles}>
          {showDragHandle && <DragHandle />}
          {priorityIndex != null && (
            <span css={priorityBadgeStyles}>{priorityIndex}</span>
          )}
        </div>
      )}
      {showCheckbox && (
        <div
          css={checkboxHitArea}
          onClick={(e) => {
            e.stopPropagation()
            onToggleCheck()
          }}
          role='checkbox'
          aria-checked={isChecked}
        >
          {isChecked ? (
            <CheckboxCheckedIcon size={20} />
          ) : (
            <CheckboxOutlineIcon size={20} />
          )}
        </div>
      )}
      <div css={rowContent}>
        <div css={rowName}>{division.name}</div>
        <div css={rowMeta}>
          {division.gamesCount > 0 && (
            <div css={metaItem}>
              <i className='fas fa-th-large' css={metaIcon} />
              <span>{division.gamesCount} games</span>
            </div>
          )}
          <div css={metaItem}>
            <i className='fas fa-shield-alt' css={metaIcon} />
            <span>
              {division.teamsCount} team{division.teamsCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
      <span css={division.isReady ? readyBadge : draftBadge}>
        <i
          className={division.isReady ? 'fas fa-check' : 'fas fa-pen'}
          css={badgeIcon}
        />
        {division.isReady ? 'Ready' : 'Draft'}
      </span>
    </div>
  )
}

// --- Styles ---

const sidebarContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 16px 12px;
  background-color: ${colors.SECONDARY_500};
`

const headerSection = css`
  padding: 0;
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`

const headerLeft = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const headerTitleRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const headerTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  color: ${colors.WHITE};
  text-transform: uppercase;
  margin: 0 0 4px 0;
`

const headerSubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  font-weight: 400;
  line-height: 17.4px;
  margin: 0;
`

const searchButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.PRIMARY};
  cursor: pointer;
  padding: 8px;
  font-size: 14px;
  flex-shrink: 0;
  &:hover {
    background-color: ${colors.PRIMARY}1A;
  }
`

const searchContainer = css`
  padding: 0 0 0;
`

const searchInput = css`
  width: 100%;
  padding: 8px 12px;
  background-color: #212935;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 6px;
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }
  &:focus {
    border-color: ${colors.PRIMARY};
  }
`

const progressCard = css`
  background-color: #212935;
  border-radius: 8px;
  padding: 8px;
`

const progressTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

const progressRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const progressCount = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

const progressBarBg = css`
  flex: 1;
  height: 8px;
  background-color: ${colors.SECONDARY_500};
  border-radius: 8px;
  overflow: hidden;
`

const progressBarFill = css`
  height: 100%;
  background-color: ${colors.SUCCESS_200};
  border-radius: 8px;
  transition: width 0.3s ease;
`

const dividerStyles = css`
  width: 100%;
  height: 1px;
  background-color: ${colors.SECONDARY_200};
  border: none;
  margin: 0;
`

const listContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  outline: none;
`

const conferenceGroup = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const conferenceHeader = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.44px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const rowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    background-color: #212935;
  }

  &:focus-visible {
    border-color: ${colors.SECONDARY_200};
  }
`

const rowSelectedStyles = css`
  border: 1px solid ${colors.PRIMARY};
`

const rowDraggableStyles = css`
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`

const rowContent = css`
  flex: 1;
  min-width: 0;
`

const rowName = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: ${colors.WHITE};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const rowMeta = css`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 2px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

const metaItem = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const metaIcon = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

const badgeIcon = css`
  font-size: 12px;
`

const draftBadge = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${colors.BLUE_GRAY};
  color: ${colors.BLUE_GRAY};
  background-color: #212935;
  white-space: nowrap;
`

const readyBadge = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${colors.SUCCESS_200};
  color: ${colors.SUCCESS_200};
  background: rgba(5, 58, 17, 0.3);
  white-space: nowrap;
`

const emptyState = css`
  padding: 24px 16px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

const checkboxHitArea = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`

const bulkToolbar = css`
  background-color: #212935;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const bulkToolbarTop = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const bulkCountText = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.WHITE};
`

const deselectLink = css`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`

const bulkToolbarActions = css`
  display: flex;
  gap: 8px;
`

const bulkReadyButton = css`
  flex: 1;
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.WHITE};
  background-color: ${colors.SUCCESS_200};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const bulkDraftButton = css`
  flex: 1;
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.WHITE};
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 6px;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// DnD-specific styles

const priorityColumnStyles = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`

const dragHandleStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 2px;
`

const priorityBadgeStyles = css`
  color: ${colors.NEUTRAL_70};
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
`

const dragOverlayCard = css`
  background: ${colors.SECONDARY_300};
  padding: 8px 16px;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${colors.PRIMARY}40;
`

const dragOverlayName = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.WHITE};
`

export default DivisionSidebar
